/**
 * Creative inventory (vanilla layout): 9x5 item grid with scrollbar, hotbar row,
 * category tabs along the top, a Search tab with a text field, and the
 * "Survival Inventory" tab (armor, offhand, backpack, preview, delete slot).
 */

import { stackName } from '../../items';
import { itemDef, maxStackOf } from '../../game/itemdb';
import { I } from '../../game/itemIds';
import { creativeCatalog } from '../../game/creativeCatalog';
import { isPotionItem, potionTooltipLines } from '../../game/brewing/potions';
import { enchTooltipLines } from '../../game/enchant/stack';
import { cloneStack, clearStack, emptyStack, isEmpty, setStack, type Stack } from '../../game/types';
import { GuiStage, type Label, mcText, sheet, sprite, textWidth } from '../kit';
import { ItemIcons } from '../itemIcons';
import { ContainerScreen, same, type SlotDef } from './container';
import type { ScreenDeps } from './screenHost';

const GRID_COLS = 9, GRID_ROWS = 5;
const PANEL_W = 195, PANEL_H = 136;

interface Tab { name: string; icon: number | 'search'; col: number; top: boolean; kind: 'items' | 'search' | 'inventory'; }

const TABS: Tab[] = [
  { name: 'Building Blocks', icon: 34, col: 0, top: true, kind: 'items' },
  { name: 'Natural Blocks', icon: 1, col: 1, top: true, kind: 'items' },
  { name: 'Functional Blocks', icon: 24, col: 2, top: true, kind: 'items' },
  { name: 'Tools & Combat', icon: I.IRON_PICKAXE, col: 3, top: true, kind: 'items' },
  { name: 'Food & Ingredients', icon: I.APPLE, col: 4, top: true, kind: 'items' },
  { name: 'Spawn Eggs', icon: I.PIG_SPAWN_EGG, col: 0, top: false, kind: 'items' },
  { name: 'Potions', icon: I.POTION, col: 1, top: false, kind: 'items' },
  { name: 'Enchanted Books', icon: I.ENCHANTED_BOOK, col: 2, top: false, kind: 'items' },
  { name: 'Search Items', icon: 'search', col: 6, top: true, kind: 'search' },
  { name: 'Survival Inventory', icon: 260, col: 6, top: false, kind: 'inventory' },
];

/** Searchable text of a stack: its name plus effect / enchantment lines. */
function searchText(st: Stack): string {
  const extra = isPotionItem(st.id) ? potionTooltipLines(st.id, st.dmg) : enchTooltipLines(st);
  return (stackName(st) || itemDef(st.id).name || '') + ' ' + extra.map((l) => l.replace(/\u00a7./, '')).join(' ');
}

const SEARCH_TEXT = new Map<Stack, string>();

const ARMOR_ICONS = ['helmet', 'chestplate', 'leggings', 'boots'].map((n) => `gui/sprites/container/slot/${n}.png`);

export class CreativeScreen extends ContainerScreen {
  private tabIdx = 0;
  private scrollOffs = 0;
  private scrolling = false;
  private query = '';
  private items: Stack[] = [];
  private readonly gridSlots: SlotDef[] = [];
  private readonly hotbarSlots: SlotDef[] = [];
  private readonly survSlots: SlotDef[] = [];
  private readonly allSlots: SlotDef[] = [];
  private readonly bgs: HTMLDivElement[] = [];
  private readonly tabEls: HTMLDivElement[] = [];
  private readonly tabIcons: HTMLDivElement[] = [];
  private readonly scroller: HTMLDivElement;
  private readonly title: Label;
  private readonly searchEl: HTMLCanvasElement;
  private readonly preview: HTMLCanvasElement;
  private hoverTab = -1;
  private blink = 0;
  private readonly trash: Stack = emptyStack();

  constructor(deps: ScreenDeps, stage: GuiStage) {
    super(deps, stage);
    this.w = PANEL_W; this.h = PANEL_H;
    this.root.style.isolation = 'isolate';

    // tabs first so the panel draws over the unselected ones
    TABS.forEach((t) => {
      const el = document.createElement('div');
      const x = this.tabX(t), y = t.top ? -28 : PANEL_H - 4;
      el.style.cssText = `position:absolute;left:${x}px;top:${y}px;width:26px;height:32px;background-size:26px 32px;image-rendering:pixelated;pointer-events:none;`;
      this.root.appendChild(el);
      this.tabEls.push(el);
      const ic = document.createElement('div');
      ic.style.cssText = `position:absolute;left:${x + 5}px;top:${y + (t.top ? 9 : 7)}px;width:16px;height:16px;pointer-events:none;z-index:6;`;
      this.root.appendChild(ic);
      this.tabIcons.push(ic);
    });
    for (const f of ['tab_items', 'tab_item_search', 'tab_inventory']) {
      const bg = sheet(`gui/container/creative_inventory/${f}.png`, 0, 0, PANEL_W, PANEL_H, 0, 0);
      this.root.appendChild(bg);
      this.bgs.push(bg);
    }

    // item grid (source slots)
    for (let r = 0; r < GRID_ROWS; r++) {
      for (let c = 0; c < GRID_COLS; c++) {
        this.gridSlots.push({ stack: emptyStack(), x: 9 + c * 18, y: 18 + r * 18, source: true, tag: 'grid' });
      }
    }
    // hotbar (shared by every tab)
    const inv = deps.inventory;
    for (let c = 0; c < 9; c++) this.hotbarSlots.push({ stack: inv.slots[c], x: 9 + c * 18, y: 112, tag: 'hotbar' });
    // survival-inventory tab
    for (let i = 0; i < 4; i++) {
      this.survSlots.push({
        stack: inv.armor[i], x: 54 + Math.floor(i / 2) * 54, y: 6 + (i % 2) * 21, maxStack: 1,
        emptyIcon: ARMOR_ICONS[i], tag: 'armor', mayPlace: (s) => itemDef(s.id).armor?.slot === i,
      });
    }
    this.survSlots.push({ stack: inv.offhand, x: 35, y: 20, emptyIcon: 'gui/sprites/container/slot/shield.png', tag: 'offhand' });
    for (let r = 0; r < 3; r++) {
      for (let c = 0; c < 9; c++) this.survSlots.push({ stack: inv.slots[9 + r * 9 + c], x: 9 + c * 18, y: 54 + r * 18, tag: 'main' });
    }
    this.survSlots.push({
      stack: this.trash, x: 173, y: 112, tag: 'trash',
      onChanged: () => { clearStack(this.trash); },
    });
    this.allSlots.push(...this.gridSlots, ...this.hotbarSlots, ...this.survSlots);
    for (const s of this.allSlots) this.buildSlotEl(s);

    this.title = this.addLabel('', 8, 6);
    this.scroller = sprite('gui/sprites/container/creative_inventory/scroller.png', 175, 18, 12, 15);
    this.root.appendChild(this.scroller);
    this.searchEl = mcText('', { color: '#ffffff', shadow: true });
    this.searchEl.style.cssText = 'position:absolute;left:83px;top:7px;pointer-events:none;image-rendering:pixelated;';
    this.root.appendChild(this.searchEl);
    this.preview = document.createElement('canvas');
    this.preview.style.cssText = 'position:absolute;left:73px;top:6px;width:32px;height:43px;pointer-events:none;';
    this.root.appendChild(this.preview);
    this.mount();
    this.selectTab(0);
  }

  private tabX(t: Tab): number {
    // columns 0..4 hug the left edge, column 5/6 the right edge (vanilla)
    return t.col <= 4 ? t.col * 27 : PANEL_W - 26 - (6 - t.col) * 27;
  }

  selectTab(i: number): void {
    this.tabIdx = i;
    const tab = TABS[i];
    this.scrollOffs = 0;
    if (tab.kind !== 'search') this.query = '';
    this.bgs.forEach((b, k) => { b.style.display = (tab.kind === 'items' ? 0 : tab.kind === 'search' ? 1 : 2) === k ? '' : 'none'; });
    this.slots.length = 0;
    const show = new Set<SlotDef>();
    if (tab.kind === 'inventory') this.survSlots.forEach((s) => show.add(s));
    else this.gridSlots.forEach((s) => show.add(s));
    this.hotbarSlots.forEach((s) => show.add(s));
    for (const s of this.allSlots) if (show.has(s)) this.slots.push(s);
    for (const s of this.allSlots) s.el!.style.display = show.has(s) ? '' : 'none';
    TABS.forEach((t, k) => {
      const el = this.tabEls[k];
      const side = t.top ? 'top' : 'bottom';
      el.style.backgroundImage =
        `url(mc/gui/sprites/container/creative_inventory/tab_${side}_${k === i ? 'selected' : 'unselected'}_${t.col + 1}.png)`;
      el.style.zIndex = k === i ? '5' : '-1';
    });
    this.title.el.style.display = tab.kind === 'items' ? '' : 'none';
    this.title.set(tab.kind === 'inventory' ? 'Inventory' : tab.name);
    this.searchEl.style.display = tab.kind === 'search' ? '' : 'none';
    this.preview.style.display = tab.kind === 'inventory' ? '' : 'none';
    this.refreshItems();
    this.refresh(true);
  }

  private refreshTabIcons(): void {
    TABS.forEach((t, k) => {
      const ic = this.tabIcons[k];
      if (ic.dataset.k) return;
      ic.dataset.k = '1';
      if (t.icon === 'search') {
        // vanilla uses the compass item as the search tab icon
        const c = document.createElement('img');
        c.src = 'mc/item/compass_16.png';
        c.style.cssText = 'position:absolute;left:0;top:0;width:16px;height:16px;image-rendering:pixelated;';
        ic.appendChild(c);
      } else {
        const c = document.createElement('canvas');
        c.width = c.height = 32;
        c.style.cssText = 'position:absolute;left:0;top:0;width:16px;height:16px;image-rendering:pixelated;';
        const src = ItemIcons.shared?.get(t.icon);
        if (src) c.getContext('2d')!.drawImage(src, 0, 0);
        ic.appendChild(c);
      }
    });
  }

  private refreshItems(): void {
    const tab = TABS[this.tabIdx];
    const cat = creativeCatalog();
    if (tab.kind === 'items') this.items = cat[this.tabIdx];
    else if (tab.kind === 'search') {
      const q = this.query.trim().toLowerCase();
      const all = cat.flat();
      this.items = q ? all.filter((st) => {
        let t = SEARCH_TEXT.get(st);
        if (t === undefined) { t = searchText(st).toLowerCase(); SEARCH_TEXT.set(st, t); }
        return t.includes(q);
      }) : all;
    } else this.items = [];
    this.fillGrid();
  }

  private totalRows(): number { return Math.ceil(this.items.length / GRID_COLS); }
  private rowOffset(): number {
    const hidden = Math.max(0, this.totalRows() - GRID_ROWS);
    return Math.round(this.scrollOffs * hidden);
  }

  private fillGrid(): void {
    const off = this.rowOffset() * GRID_COLS;
    this.gridSlots.forEach((s, i) => {
      const it = this.items[off + i];
      if (it === undefined) clearStack(s.stack);
      else setStack(s.stack, it);
    });
    const scrollable = this.totalRows() > GRID_ROWS && TABS[this.tabIdx].kind !== 'inventory';
    const inv = TABS[this.tabIdx].kind === 'inventory';
    this.scroller.style.display = inv ? 'none' : '';
    this.scroller.style.top = 18 + Math.round(this.scrollOffs * 75) + 'px';
    this.scroller.style.backgroundImage = `url(mc/gui/sprites/container/creative_inventory/${scrollable ? 'scroller' : 'scroller_disabled'}.png)`;
  }

  wheel(deltaY: number): void {
    const hidden = this.totalRows() - GRID_ROWS;
    if (hidden <= 0 || TABS[this.tabIdx].kind === 'inventory') return;
    this.scrollOffs = Math.min(1, Math.max(0, this.scrollOffs + (deltaY > 0 ? 1 : -1) / hidden));
    this.fillGrid();
    this.refresh(false);
  }

  // --------------------------------------------------------------- input ----

  private tabAt(lx: number, ly: number): number {
    for (let i = 0; i < TABS.length; i++) {
      const t = TABS[i];
      const x = this.tabX(t);
      const y0 = t.top ? -28 : PANEL_H, y1 = t.top ? 0 : PANEL_H + 28;
      if (lx >= x && lx < x + 26 && ly >= y0 && ly < y1) return i;
    }
    return -1;
  }

  override pointerMove(cx: number, cy: number): void {
    super.pointerMove(cx, cy);
    const lx = this.mx - this.left, ly = this.my - this.top;
    if (this.scrolling) this.dragScroll(ly);
    const t = this.tabAt(lx, ly);
    if (t !== this.hoverTab) { this.hoverTab = t; }
  }

  protected override tipLines(): string[] | null {
    if (this.hoverTab >= 0) return [TABS[this.hoverTab].name];
    return super.tipLines();
  }

  private dragScroll(ly: number): void {
    this.scrollOffs = Math.min(1, Math.max(0, (ly - 18 - 7.5) / 75));
    this.fillGrid();
    this.refresh(false);
  }

  override pointerDown(button: number, shift: boolean): void {
    const lx = this.mx - this.left, ly = this.my - this.top;
    if (button === 0) {
      const t = this.tabAt(lx, ly);
      if (t >= 0) { this.selectTab(t); return; }
      if (TABS[this.tabIdx].kind !== 'inventory' && lx >= 175 && lx < 187 && ly >= 18 && ly < 108) {
        this.scrolling = true;
        this.dragScroll(ly);
        return;
      }
    }
    super.pointerDown(button, shift);
  }

  override pointerUp(button: number): void {
    this.scrolling = false;
    super.pointerUp(button);
  }

  override keyDown(e: KeyboardEvent): boolean {
    if (TABS[this.tabIdx].kind !== 'search') return false;
    if (e.ctrlKey || e.metaKey || e.altKey) return false;
    if (e.key === 'Backspace') { this.query = this.query.slice(0, -1); }
    else if (e.key.length === 1 && textWidth(this.query + e.key) < 86) { this.query += e.key; }
    else return false;
    this.scrollOffs = 0;
    this.refreshItems();
    this.refresh(false);
    return true;
  }
  /** True while keystrokes should go to the search field (E must not close the screen). */
  get typing(): boolean { return TABS[this.tabIdx].kind === 'search'; }

  // creative list clicks -------------------------------------------------------

  protected override clickSource(slot: SlotDef, button: number): void {
    const cur = this.cursor, st = slot.stack;
    if (isEmpty(st)) { clearStack(cur); this.changed(); return; }
    const max = maxStackOf(st.id);
    if (isEmpty(cur)) {
      setStack(cur, { ...cloneStack(st), count: button === 1 ? 1 : max });
    } else if (same(cur, st)) {
      if (button === 1) { cur.count -= 1; if (cur.count <= 0) clearStack(cur); }
      else cur.count = Math.min(max, cur.count + 1);
    } else {
      clearStack(cur);
    }
    this.changed();
  }

  protected override quickMoveSource(slot: SlotDef): void {
    if (isEmpty(slot.stack)) return;
    this.deps.inventory.add(slot.stack.id, maxStackOf(slot.stack.id), slot.stack.dmg, slot.stack.ench);
    this.changed();
  }

  protected quickMoveStack(i: number): void {
    const slot = this.slots[i];
    if (TABS[this.tabIdx].kind !== 'inventory') {
      // item tabs: shift-click in the hotbar deletes (vanilla)
      if (slot.tag === 'hotbar') clearStack(slot.stack);
      return;
    }
    const s = slot.stack;
    const mainStart = this.slots.findIndex((x) => x.tag === 'main');
    const hotStart = this.slots.findIndex((x) => x.tag === 'hotbar');
    if (slot.tag === 'trash') return;
    if (slot.tag === 'armor' || slot.tag === 'offhand') { this.moveTo(s, mainStart, hotStart + 9, false); return; }
    const a = itemDef(s.id).armor;
    if (a && isEmpty(this.slots[a.slot].stack) && this.moveTo(s, a.slot, a.slot + 1, false)) return;
    if (slot.tag === 'main') this.moveTo(s, hotStart, hotStart + 9, false);
    else this.moveTo(s, mainStart, hotStart, false);
  }

  override hotbarSwap(n: number): boolean {
    const slot = this.hover;
    if (slot?.source) {
      if (isEmpty(slot.stack)) return true;
      setStack(this.deps.inventory.slots[n], { ...cloneStack(slot.stack), count: maxStackOf(slot.stack.id) });
      this.changed();
      this.refresh(false);
      return true;
    }
    return super.hotbarSwap(n);
  }

  override update(dt: number): void {
    this.refreshTabIcons();
    if (TABS[this.tabIdx].kind === 'search') {
      this.blink += dt;
      const txt = this.query + (Math.floor(this.blink * 2) % 2 === 0 ? '_' : '');
      const c = mcText(txt, { color: '#ffffff', shadow: true });
      if (this.searchEl.dataset.t !== txt) {
        this.searchEl.dataset.t = txt;
        this.searchEl.width = c.width; this.searchEl.height = c.height;
        this.searchEl.getContext('2d')!.drawImage(c, 0, 0);
        this.searchEl.style.width = c.width + 'px'; this.searchEl.style.height = c.height + 'px';
      }
    }
    if (TABS[this.tabIdx].kind === 'inventory') {
      const S = this.stage.scale;
      const w = Math.round(32 * S), h = Math.round(43 * S);
      if (this.preview.width !== w || this.preview.height !== h) { this.preview.width = w; this.preview.height = h; }
      this.deps.renderPlayerPreview(this.preview, this.left + 88 - this.mx, this.top + 45 - 30 - this.my);
    }
  }

  protected override onClose(): void {
    clearStack(this.trash);
    this.query = '';
  }
}
